import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Handshake, Search, Plus, RefreshCcw, TrendingUp, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, Shield, Layers, Clock, Target, Award, MessageSquare } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, useAchatsListViewMode, useAchatsListDisplay, fetchAchatsSuppliers } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const NegociationsPage = () => {
  const { t } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.negociationsPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();

  const [searchTerm, setSearchTerm] = useState('');
  const [negotiations, setNegotiations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingNegotiation, setEditingNegotiation] = useState(null);
  const [selectedNegotiations, setSelectedNegotiations] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingNegotiation, setViewingNegotiation] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic_info'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'parties', label: p('section_parties'), icon: Users, iconColor: 'text-green-400' },
    { id: 'notes', label: p('section_notes'), icon: FileText, iconColor: 'text-amber-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    negotiation_reference: '', 
    negotiation_subject: '',
    negotiation_type: 'price',
    status: 'initiated',
    start_date: new Date().toISOString().split('T')[0], 
    end_date: '', 
    target_deadline: '',
    initial_offer: '', 
    counter_offer: '',
    final_agreement: '',
    savings_achieved: '',
    supplier_id: '',
    supplier_name: '',
    supplier_contact: '',
    our_representative: '',
    category: '',
    priority: 'medium',
    department: '',
    current_round: '',
    total_rounds: '',
    success_probability: '',
    description: '',
    terms_discussed: '',
    concessions_made: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_negotiations');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Dropdown data states
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadNegotiations(); 
    loadSuppliers();
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNegotiations = async () => {
    try {
      const response = await localApiAdapter.get('/achats/negociations');
      setNegotiations(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading negotiations:', error);
      setNegotiations([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData(prev => ({ ...prev, supplier_id: '', supplier_name: '', supplier_contact: '' }));
      return;
    }
    const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
    if (supplier) {
      setFormData(prev => ({ 
        ...prev, 
        supplier_id: supplierId,
        supplier_name: supplier.company_name || supplier.name || '',
        supplier_contact: supplier.contact_person || supplier.phone1 || ''
      }));
    }
  };

  const stats = {
    total: negotiations.length,
    initiated: negotiations.filter(n => n.status === 'initiated').length,
    ongoing: negotiations.filter(n => n.status === 'ongoing').length,
    completed: negotiations.filter(n => n.status === 'completed').length,
    totalSavings: negotiations.reduce((sum, n) => sum + (parseFloat(n.savings_achieved) || 0), 0),
    avgSuccess: negotiations.length > 0 ? (negotiations.reduce((sum, n) => sum + (parseFloat(n.success_probability) || 0), 0) / negotiations.length).toFixed(1) : 0
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_negotiations', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('mainTab_by_type'), icon: Shield },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: Target },
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'initiated', name: p('quickFilter_initiated'), icon: MessageSquare },
    { id: 'ongoing', name: p('quickFilter_ongoing'), icon: Clock },
    { id: 'completed', name: p('quickFilter_completed'), icon: CheckCircle },
  ]), [t]);

  const negotiationDistribution = useMemo(() => ({
    all_negotiations: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'initiated', name: p('dist_initiated'), count: stats.initiated, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'ongoing', name: p('dist_ongoing'), count: stats.ongoing, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [
        { filterKey: 'price', name: p('dist_price'), count: negotiations.filter(n => n.negotiation_type === 'price').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'terms', name: p('dist_terms'), count: negotiations.filter(n => n.negotiation_type === 'terms').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'contract', name: p('dist_contract'), count: negotiations.filter(n => n.negotiation_type === 'contract').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'other', name: p('dist_other'), count: negotiations.filter(n => !['price', 'terms', 'contract'].includes(n.negotiation_type)).length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'initiated', name: p('dist_initiated'), count: stats.initiated, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'ongoing', name: p('dist_ongoing'), count: stats.ongoing, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  }), [t, stats.initiated, stats.ongoing, stats.completed, negotiations]);

  const distributionInfo = negotiationDistribution[mainViewMode] || negotiationDistribution.all_negotiations;

  const baseFilteredNegotiations = negotiations.filter(n => {
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const negotiationDate = new Date(n.start_date);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (negotiationDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (negotiationDate > endOfDay) return false;
      }
    }
    
    // Search filter
    if (searchTerm && !n.negotiation_subject?.toLowerCase().includes(searchTerm.toLowerCase()) && !n.negotiation_reference?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_negotiations') {
      if (quickFilter === 'initiated' && n.status !== 'initiated') return false;
      if (quickFilter === 'ongoing' && n.status !== 'ongoing') return false;
      if (quickFilter === 'completed' && n.status !== 'completed') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      if (mainViewMode === 'all_negotiations' || mainViewMode === 'par_statut') {
        if (n.status !== activeDistributionFilter) return false;
      } else if (mainViewMode === 'par_type') {
        if (activeDistributionFilter === 'other') {
          if (['price', 'terms', 'contract'].includes(n.negotiation_type)) return false;
        } else if (n.negotiation_type !== activeDistributionFilter) {
          return false;
        }
      }
    }
    
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        negotiation_reference: formData.negotiation_reference,
        negotiation_subject: formData.negotiation_subject,
        negotiation_type: formData.negotiation_type,
        status: formData.status,
        start_date: formData.start_date,
        end_date: formData.end_date,
        target_deadline: formData.target_deadline,
        initial_offer: parseFloat(formData.initial_offer) || 0,
        counter_offer: parseFloat(formData.counter_offer) || 0,
        final_agreement: parseFloat(formData.final_agreement) || 0,
        savings_achieved: parseFloat(formData.savings_achieved) || 0,
        supplier_name: formData.supplier_name,
        supplier_contact: formData.supplier_contact,
        our_representative: formData.our_representative,
        category: formData.category,
        priority: formData.priority,
        department: formData.department,
        current_round: parseInt(formData.current_round) || 0,
        total_rounds: parseInt(formData.total_rounds) || 0,
        success_probability: parseFloat(formData.success_probability) || 0,
        description: formData.description,
        terms_discussed: formData.terms_discussed,
        concessions_made: formData.concessions_made,
        notes: formData.notes
      };
      if (editingNegotiation) {
        await localApiAdapter.put(`/achats/negociations/${editingNegotiation.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/negociations', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error') + (error.message || p('unknown_error')));
    } finally {
      resetForm();
      loadNegotiations();
    }
  };

  const handleEdit = (negotiation) => {
    setEditingNegotiation(negotiation);
    setFormData({ 
      negotiation_reference: negotiation.negotiation_reference || '',
      negotiation_subject: negotiation.negotiation_subject || '',
      negotiation_type: negotiation.negotiation_type || 'price',
      status: negotiation.status || 'initiated',
      start_date: negotiation.start_date || '',
      end_date: negotiation.end_date || '',
      target_deadline: negotiation.target_deadline || '',
      initial_offer: negotiation.initial_offer?.toString() || '',
      counter_offer: negotiation.counter_offer?.toString() || '',
      final_agreement: negotiation.final_agreement?.toString() || '',
      savings_achieved: negotiation.savings_achieved?.toString() || '',
      supplier_name: negotiation.supplier_name || '',
      supplier_contact: negotiation.supplier_contact || '',
      our_representative: negotiation.our_representative || '',
      category: negotiation.category || '',
      priority: negotiation.priority || 'medium',
      department: negotiation.department || '',
      current_round: negotiation.current_round?.toString() || '',
      total_rounds: negotiation.total_rounds?.toString() || '',
      success_probability: negotiation.success_probability?.toString() || '',
      description: negotiation.description || '',
      terms_discussed: negotiation.terms_discussed || '',
      concessions_made: negotiation.concessions_made || '',
      notes: negotiation.notes || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/negociations/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadNegotiations();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { defaultValue: p('unknown_error') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedNegotiations.length }))) {
      try {
        const deleteCount = selectedNegotiations.length;
        await Promise.all(selectedNegotiations.map(id => localApiAdapter.delete(`/achats/negociations/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedNegotiations([]);
        loadNegotiations();
      } catch (error) {
        console.error('Error deleting negotiations:', error);
        notifyError(showNotify, p('save_error', { defaultValue: p('unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      negotiation_reference: '', 
      negotiation_subject: '',
      negotiation_type: 'price',
      status: 'initiated',
      start_date: '', 
      end_date: '', 
      target_deadline: '',
      initial_offer: '', 
      counter_offer: '',
      final_agreement: '',
      savings_achieved: '',
      supplier_name: '',
      supplier_contact: '',
      our_representative: '',
      category: '',
      priority: 'medium',
      department: '',
      current_round: '',
      total_rounds: '',
      success_probability: '',
      description: '',
      terms_discussed: '',
      concessions_made: '',
      notes: ''
    });
    setEditingNegotiation(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'negotiation_reference', label: p('col_reference') },
      { key: 'negotiation_subject', label: p('col_subject') },
      { key: 'negotiation_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'start_date', label: p('label_start_date') },
      { key: 'initial_offer', label: p('label_initial_offer') },
      { key: 'final_agreement', label: p('label_final_agreement') },
      { key: 'savings_achieved', label: p('col_savings') }
    ];
    const fileName = `negociations_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredNegotiations, headers, `${fileName}.xlsx`);
  };

  const handleImport = () => {
    const headers = [
      { key: 'negotiation_reference', label: p('col_reference') },
      { key: 'negotiation_subject', label: p('col_subject') },
      { key: 'negotiation_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'start_date', label: p('label_start_date') },
      { key: 'initial_offer', label: p('label_initial_offer') }
    ];
    const schema = {
      requiredFields: ['negotiation_reference', 'negotiation_subject'],
      validators: {
        initial_offer: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/negociations', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadNegotiations();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedNegotiations([]);
    } else {
      setSelectedNegotiations(filteredNegotiations.map((r) => r.id));
    }
  };
  
  const handleSelectNegotiation = (id) => { 
    setSelectedNegotiations(selectedNegotiations.includes(id) ? selectedNegotiations.filter(nId => nId !== id) : [...selectedNegotiations, id]); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/negociations/${recordId}`, { status: newStatus });
      await loadNegotiations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const negotiationTypeOptions = useMemo(() => ([
    { value: 'price', label: p('type_price') },
    { value: 'terms', label: p('type_terms') },
    { value: 'contract', label: p('type_contract') },
    { value: 'delivery', label: p('type_delivery') },
    { value: 'quality', label: p('type_quality') },
    { value: 'payment', label: p('type_payment') },
  ]), [t]);

  const statusOptions = useMemo(() => ([
    { value: 'initiated', label: p('status_initiated'), icon: MessageSquare, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'ongoing', label: p('status_ongoing'), icon: Clock, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'failed', label: p('status_failed'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: AlertCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  ]), [p]);

  const priorityOptions = useMemo(() => ([
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') },
  ]), [t]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('negotiation_reference'),
      textColumnRule('negotiation_subject'),
      exactColumnRule('negotiation_type', (r) => r.negotiation_type || ''),
      textColumnRule('supplier_name'),
      textColumnRule('initial_offer', (r) => String(r.initial_offer ?? '')),
      textColumnRule('savings_achieved', (r) => String(r.savings_achieved ?? '')),
      exactColumnRule('priority', (r) => r.priority || ''),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [],
  );

  const { filtered: filteredNegotiations, displayed: displayedNegotiations } = useAchatsListDisplay(
    baseFilteredNegotiations,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedNegotiations, filteredNegotiations);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const categoryOptions = useMemo(() => ([
    { value: 'matieres_premieres', label: p('category_matiere_premiere') },
    { value: 'equipements', label: p('category_equipements') },
    { value: 'services', label: p('category_services') },
    { value: 'transport', label: p('category_transport') },
  ]), [t]);

  const negociationsColumnKeys = useMemo(
    () => ['negotiation_reference', 'negotiation_subject', 'negotiation_type', 'supplier_name', 'initial_offer', 'savings_achieved', 'priority', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(negociationsColumnKeys);
  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'negotiation_reference', labelKey: 'col_reference' },
      { key: 'negotiation_subject', labelKey: 'col_subject' },
      { key: 'negotiation_type', labelKey: 'col_type' },
      { key: 'supplier_name', labelKey: 'col_supplier' },
      { key: 'initial_offer', labelKey: 'label_initial_offer' },
      { key: 'savings_achieved', labelKey: 'col_savings' },
      { key: 'priority', labelKey: 'col_priority' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Handshake className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadNegotiations} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" 
            onClick={() => { setEditingNegotiation(null); setFormTab('general');
    setShowModal(true); }}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: Handshake, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_initiated'), value: stats.initiated, icon: MessageSquare, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_ongoing'), value: stats.ongoing, icon: Clock, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_completed'), value: stats.completed, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_total_savings'), value: formatBrandMoney(stats.totalSavings), icon: TrendingDown, color: 'from-red-500 to-red-600' },
          { title: p('stat_success_rate'), value: `${stats.avgSuccess}%`, icon: Award, color: 'from-purple-500 to-purple-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <motion.div 
        initial={false} 
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} 
        transition={{ duration: 0.3, ease: "easeInOut" }} 
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-6 pt-6">
          {/* Main Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button 
                  key={tab.id} 
                  onClick={() => { 
                    setMainViewMode(tab.id); 
                    setQuickFilter('all'); 
                    setActiveDistributionFilter(null); 
                  }} 
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Buttons (only for Vue Générale) */}
          {mainViewMode === 'all_negotiations' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button 
                    key={filter.id} 
                    onClick={() => setQuickFilter(filter.id)} 
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id 
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.filterKey || item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.name 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_by_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>
                      {item.name}
                    </h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div 
                      className={`h-3 rounded-full ${item.color} transition-all`} 
                      style={{ 
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` 
                      }}
                    ></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}${p('percent_of_negotiations')}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Table Container - TO BE CONTINUED IN NEXT PART... */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <AchatsBulkDeleteBar
          selectedCount={selectedNegotiations.length}
          onDelete={handleBulkDelete}
          deleteLabel={`${t('common.delete')} (${selectedNegotiations.length})`}
        />

        {/* Table View - Due to length, continued in separate message */}
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('negotiation_reference') ? <TableColumnHeaderFilter {...colHeader('negotiation_reference', p('col_reference'))} /> : null}
                  {isColumnVisible('negotiation_subject') ? <TableColumnHeaderFilter {...colHeader('negotiation_subject', p('col_subject'))} /> : null}
                  {isColumnVisible('negotiation_type') ? <TableColumnHeaderFilter {...colHeader('negotiation_type', p('col_type'), { kind: 'select', options: negotiationTypeOptions })} /> : null}
                  {isColumnVisible('supplier_name') ? <TableColumnHeaderFilter {...colHeader('supplier_name', p('col_supplier'))} /> : null}
                  {isColumnVisible('initial_offer') ? <TableColumnHeaderFilter {...colHeader('initial_offer', p('label_initial_offer'))} /> : null}
                  {isColumnVisible('savings_achieved') ? <TableColumnHeaderFilter {...colHeader('savings_achieved', p('col_savings'), { align: 'center' })} /> : null}
                  {isColumnVisible('priority') ? <TableColumnHeaderFilter {...colHeader('priority', p('col_priority'), { kind: 'select', options: priorityOptions, align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedNegotiations.length > 0 ? (
                  displayedNegotiations.map((negotiation, index) => (
                    <tr 
                      key={negotiation.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedNegotiations.includes(negotiation.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedNegotiations.includes(negotiation.id)}
                              onChange={() => handleSelectNegotiation(negotiation.id)}
                            />
                          </div>
                        </td>
                      {isColumnVisible('negotiation_reference') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{negotiation.negotiation_reference}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('negotiation_subject') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{negotiation.negotiation_subject}</span>
                          <span className="text-white/50 text-xs mt-0.5">{negotiation.category || p('uncategorized')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('negotiation_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/80">{negotiationTypeOptions.find(t => t.value === negotiation.negotiation_type)?.label || negotiation.negotiation_type}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('supplier_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{negotiation.supplier_name || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{negotiation.supplier_contact || ''}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('initial_offer') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatBrandMoney((negotiation.initial_offer || 0))}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('card_final_agreement')} {formatBrandMoney((negotiation.final_agreement || 0))}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('savings_achieved') ? (
                      <td className="px-6 py-4">
                        <span className="text-green-300 font-semibold">{formatBrandMoney((negotiation.savings_achieved || 0))}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('priority') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">{getPriorityBadge(negotiation.priority)}</div>
                      </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={negotiation.status} recordId={negotiation.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingNegotiation(negotiation); setShowViewModal(true); }}
                          onEdit={() => handleEdit(negotiation)}
                          onDelete={() => handleDelete(negotiation.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Handshake size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedNegotiations.length > 0 ? (
              displayedNegotiations.map((negotiation, index) => (
                <motion.div
                  key={negotiation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Handshake size={20} className="text-blue-400" />
                          {negotiation.negotiation_subject}
                        </h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge status={negotiation.status} recordId={negotiation.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          {getPriorityBadge(negotiation.priority)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_reference')}:</span> {negotiation.negotiation_reference}</div>
                            <div><span className="font-medium">{p('col_type')}:</span> {negotiationTypeOptions.find(t => t.value === negotiation.negotiation_type)?.label}</div>
                            <div><span className="font-medium">{p('label_category')}:</span> {negotiation.category || p('uncategorized')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_parties')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_supplier_name')}:</span> {negotiation.supplier_name || '-'}</div>
                            <div><span className="font-medium">{p('label_responsible')}:</span> {negotiation.our_representative || '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_initial_offer')}</span> {formatBrandMoney((negotiation.initial_offer || 0))}</div>
                            <div><span className="font-medium">{p('card_final_agreement')}</span> {formatBrandMoney((negotiation.final_agreement || 0))}</div>
                            <div><span className="font-medium">{p('card_savings')}</span> <span className="text-green-300">{formatBrandMoney((negotiation.savings_achieved || 0))}</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_progress')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_round')}</span> {negotiation.current_round || 0}/{negotiation.total_rounds || 0}</div>
                            <div><span className="font-medium">{p('label_success_probability')}:</span> {negotiation.success_probability || 0}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingNegotiation(negotiation); setShowViewModal(true); }}
                      onEdit={() => handleEdit(negotiation)}
                      onDelete={() => handleDelete(negotiation.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Handshake size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && viewingNegotiation ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_modal_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_general_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('col_reference')}</p>
                      <p className={m.viewValue}>{viewingNegotiation.negotiation_reference}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_subject')}</p>
                      <p className={m.viewValue}>{viewingNegotiation.negotiation_subject}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_type')}</p>
                      <p className={m.viewValue}>{negotiationTypeOptions.find(t => t.value === viewingNegotiation.negotiation_type)?.label}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_status')}</p>
                      <div className="flex">{getStatusBadge(viewingNegotiation.status)}</div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingNegotiation.priority)}</div>
                    </div>
                  </div>
                </div>

                {viewingNegotiation.description && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_description')}</h3>
                    <p className="text-white/80">{viewingNegotiation.description}</p>
                  </div>
                )}

                {viewingNegotiation.terms_discussed && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_terms_discussed')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingNegotiation.terms_discussed}</p>
                  </div>
                )}

                {viewingNegotiation.concessions_made && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_concessions')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingNegotiation.concessions_made}</p>
                  </div>
                )}

                {viewingNegotiation.notes && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_notes')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingNegotiation.notes}</p>
                  </div>
                )}
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal - WITH CUSTOM COMPONENTS - Due to length limit, showing structure only */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingNegotiation ? p('modal_edit') : p('modal_new')}
        icon={Handshake}
        formId="negotiation-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingNegotiation ? p('modal_edit') : p('modal_new')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="negotiation-form" onSubmit={handleSubmit}>
              {formTab === 'general' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_reference_required')} /></label>
                    <input
                      type="text"
                      required
                      value={formData.negotiation_reference}
                      onChange={(e) => setFormData({ ...formData, negotiation_reference: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_ref')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_subject_required')} /></label>
                    <input
                      type="text"
                      required
                      value={formData.negotiation_subject}
                      onChange={(e) => setFormData({ ...formData, negotiation_subject: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_subject')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_negotiation_type_required')} /></label>
                    <CustomSelect
                      value={formData.negotiation_type}
                      onChange={(value) => setFormData({ ...formData, negotiation_type: value })}
                      options={negotiationTypeOptions}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_status_required')} /></label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                      options={statusOptions.map((option) => ({ value: option.value, label: option.label }))}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_priority_required')} /></label>
                    <CustomSelect
                      value={formData.priority}
                      onChange={(value) => setFormData({ ...formData, priority: value })}
                      options={priorityOptions}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_category')}</label>
                    <CustomSelect
                      value={formData.category}
                      onChange={(value) => setFormData({ ...formData, category: value })}
                      options={categoryOptions}
                      placeholder={p('select_or_type')}
                      searchable
                      creatable
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_department')}</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_department')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_responsible')}</label>
                    <input
                      type="text"
                      value={formData.our_representative}
                      onChange={(e) => setFormData({ ...formData, our_representative: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_representative')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_start_date')}</label>
                    <CustomDatePicker
                      value={formData.start_date}
                      onChange={(date) => setFormData({ ...formData, start_date: date })}
                      placeholder={p('select_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_end_date')}</label>
                    <CustomDatePicker
                      value={formData.end_date}
                      onChange={(date) => setFormData({ ...formData, end_date: date })}
                      placeholder={p('select_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_target_deadline')}</label>
                    <CustomDatePicker
                      value={formData.target_deadline}
                      onChange={(date) => setFormData({ ...formData, target_deadline: date })}
                      placeholder={p('select_date')}
                    />
                  </div>
                </div>
              )}

              {formTab === 'parties' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}>{p('label_supplier')}</label>
                    {loadingSuppliers ? (
                      <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                    ) : (
                      <CustomSelect
                        value={formData.supplier_id}
                        onChange={(value) => handleSupplierSelection(value)}
                        options={availableSuppliers.map((supplier) => ({
                          value: supplier.id,
                          label: `${supplier.company_name || supplier.name || p('label_name')} - ${supplier.contact_person || supplier.phone1 || ''}`,
                        }))}
                        placeholder={p('select_or_type')}
                        searchable
                      />
                    )}
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_supplier_name')}</label>
                    <input
                      type="text"
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_supplier_name')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_supplier_contact')}</label>
                    <input
                      type="text"
                      value={formData.supplier_contact}
                      onChange={(e) => setFormData({ ...formData, supplier_contact: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('ph_supplier_contact')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_description')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_terms_discussed')}</label>
                    <textarea
                      value={formData.terms_discussed}
                      onChange={(e) => setFormData({ ...formData, terms_discussed: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_terms')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_concessions')}</label>
                    <textarea
                      value={formData.concessions_made}
                      onChange={(e) => setFormData({ ...formData, concessions_made: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_concessions')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_initial_offer')}</label>
                    <CustomNumberInput
                      value={formData.initial_offer}
                      onChange={(value) => setFormData({ ...formData, initial_offer: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_counter_offer')}</label>
                    <CustomNumberInput
                      value={formData.counter_offer}
                      onChange={(value) => setFormData({ ...formData, counter_offer: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_final_agreement')}</label>
                    <CustomNumberInput
                      value={formData.final_agreement}
                      onChange={(value) => setFormData({ ...formData, final_agreement: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_savings')}</label>
                    <CustomNumberInput
                      value={formData.savings_achieved}
                      onChange={(value) => setFormData({ ...formData, savings_achieved: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_current_round')}</label>
                    <CustomNumberInput
                      value={formData.current_round}
                      onChange={(value) => setFormData({ ...formData, current_round: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_total_rounds')}</label>
                    <CustomNumberInput
                      value={formData.total_rounds}
                      onChange={(value) => setFormData({ ...formData, total_rounds: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_success_probability')}</label>
                    <CustomNumberInput
                      value={formData.success_probability}
                      onChange={(value) => setFormData({ ...formData, success_probability: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                      max={100}
                      decimals={1}
                    />
                  </div>
                </div>
              )}

              {formTab === 'notes' && (
                <div className={ACHATS_FORM_GRID}>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_notes')}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[150px]"
                      placeholder={p('placeholder_notes')}
                    />
                  </div>
                </div>
              )}

              </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default NegociationsPage;

